/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import { useAuth } from "context/AuthContext";
import { getVisibleRoutes } from "./filterRoutes";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

import { DARK_LOGO_FILTER } from "utils/lightDark";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const { hasPermission } = useAuth();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, darkMode, sidenavColor } = controller;
  // The white sidenav is a light-mode look; dark mode uses the dark sidenav.
  const whiteSidenav = controller.whiteSidenav && !darkMode;
  const location = useLocation();
  // A page is active on its own path and on its sub-pages (/bootcamps/123), matched by route
  // rather than by key (the key and the path differ, e.g. voice-calls vs /calls).
  const isActiveRoute = (route) =>
    !!route && (location.pathname === route || location.pathname.startsWith(`${route}/`));
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({});

  let textColor = "white";

  if (transparentSidenav || whiteSidenav) {
    textColor = "dark";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, true);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const toggleSection = (key) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Filter routes based on permissions and search query — also drops a
  // section's divider/title when none of its pages are permitted, so users
  // don't see empty section headers with nothing underneath them.
  const filteredRoutes = getVisibleRoutes(routes, hasPermission, searchQuery);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  // Which section heading each page sits under, so a folded section hides its pages.
  // While searching every match stays visible.
  let currentSection = null;
  const sectionOf = filteredRoutes.map((r) => {
    if (r.type === "title") currentSection = r.key;
    else if (r.type === "divider") currentSection = null;
    return r.type === "collapse" ? currentSection : null;
  });

  const renderRoutes = filteredRoutes.map(
    (
      { type, name, icon, title, noCollapse, key, href, route, badge, badgeColor, description },
      index
    ) => {
      let returnValue;

      if (type === "collapse") {
        if (!searchQuery && sectionOf[index] && collapsedSections[sectionOf[index]]) return null;
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={isActiveRoute(route)}
              noCollapse={noCollapse}
              badge={badge}
              badgeColor={badgeColor}
              description={description}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={isActiveRoute(route)}
              badge={badge}
              badgeColor={badgeColor}
              description={description}
            />
          </NavLink>
        );
      } else if (type === "title") {
        const nextRoutes = filteredRoutes.slice(index + 1);
        const sectionRoutes = [];
        for (let i = 0; i < nextRoutes.length; i++) {
          if (nextRoutes[i].type === "title" || nextRoutes[i].type === "divider") break;
          if (nextRoutes[i].type === "collapse") sectionRoutes.push(nextRoutes[i]);
        }

        const isCollapsed = collapsedSections[key];

        returnValue = (
          <MDBox key={key}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => toggleSection(key)}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
                transition: "opacity 0.2s",
              }}
            >
              <MDTypography
                color={textColor}
                display="block"
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                pl={3}
                mt={2}
                mb={1}
                ml={1}
                sx={{
                  color: transparentSidenav || whiteSidenav ? "#344767" : "#ffffff !important",
                }}
              >
                {title}
              </MDTypography>
              {!miniSidenav && (
                <Icon
                  fontSize="small"
                  sx={{
                    mr: 2,
                    transition: "transform 0.3s",
                    transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                    // "white" isn't a palette colour, so on the dark sidenav it fell back to navy.
                    color: transparentSidenav || whiteSidenav ? textColor : "#ffffff",
                  }}
                >
                  {isCollapsed ? "chevron_right" : "expand_more"}
                </Icon>
              )}
            </MDBox>
          </MDBox>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
            sx={{ my: 1 }}
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <>
      {!miniSidenav && (
        <MDBox
          onClick={closeSidenav}
          aria-hidden="true"
          sx={({ zIndex }) => ({
            display: { xs: "block", xl: "none" },
            position: "fixed",
            inset: 0,
            zIndex: zIndex.drawer - 1,
            backgroundColor: "rgba(0, 0, 0, 0.35)",
          })}
        />
      )}
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      >
        <MDBox pt={3} pb={1} px={4} textAlign="center">
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </MDTypography>
          </MDBox>
          <MDBox
            component={NavLink}
            to="/dashboard"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {brand && (
              <MDBox
                component="img"
                src={brand}
                alt="Brand"
                sx={{
                  width: miniSidenav ? "2.5rem" : "7rem",
                  height: miniSidenav ? "2.5rem" : "7rem",
                  objectFit: "contain",
                  transition: "all 0.3s ease",
                  mr: miniSidenav ? 0 : 1.5,
                  // App passes the built-in navy logo, which vanishes on the dark sidenav.
                  filter: darkMode ? DARK_LOGO_FILTER : "none",
                }}
              />
            )}
          </MDBox>
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
        {!miniSidenav && (
          <MDBox px={2} pt={2} pb={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon fontSize="small" sx={{ color: textColor }}>
                      search
                    </Icon>
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <Icon fontSize="small" sx={{ color: textColor }}>
                        close
                      </Icon>
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  fontSize: "0.875rem",
                  backgroundColor:
                    transparentSidenav || whiteSidenav
                      ? "rgba(0,0,0,0.05)"
                      : "rgba(255,255,255,0.1)",
                  color: textColor,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      transparentSidenav || whiteSidenav
                        ? "rgba(0,0,0,0.2)"
                        : "rgba(255,255,255,0.2)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      transparentSidenav || whiteSidenav
                        ? "rgba(0,0,0,0.3)"
                        : "rgba(255,255,255,0.3)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      transparentSidenav || whiteSidenav
                        ? "rgba(0,0,0,0.5)"
                        : "rgba(255,255,255,0.5)",
                  },
                },
              }}
              sx={{
                "& .MuiInputBase-input": {
                  color: textColor,
                  "&::placeholder": {
                    color: textColor,
                    opacity: 0.5,
                  },
                },
              }}
            />
          </MDBox>
        )}
        <List>{renderRoutes}</List>
        <MDBox px={2} pb={2} mt="auto">
          <Divider
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
            sx={{ mb: 2 }}
          />
          {!miniSidenav && (
            <MDBox display="flex" flexDirection="column" gap={1}>
              <MDTypography
                variant="caption"
                color={textColor}
                fontWeight="medium"
                sx={{
                  opacity: 0.7,
                  px: 1,
                  color: transparentSidenav || whiteSidenav ? "#344767" : "#ffffff !important",
                }}
              >
                Quick Actions
              </MDTypography>
              <MDButton
                component={NavLink}
                to="/settings"
                variant="outlined"
                color={sidenavColor}
                size="small"
                fullWidth
                startIcon={<Icon>settings</Icon>}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                Settings
              </MDButton>
              <MDButton
                component={NavLink}
                to="/profile"
                variant="text"
                color={sidenavColor}
                size="small"
                fullWidth
                startIcon={<Icon>person</Icon>}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                My Profile
              </MDButton>
            </MDBox>
          )}
        </MDBox>
      </SidenavRoot>
    </>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
